import { z } from 'zod/v4';

export const RemindQuestionnaireRequestsParamsSchema = z.object({
  id: z.string().min(1),
});

export type RemindQuestionnaireRequestsParams = z.infer<
  typeof RemindQuestionnaireRequestsParamsSchema
>;

/**
 * Body of `PATCH /api/questionnaires/:id/requests/reminder`, ported from the
 * legacy `bulkReminder.body` JSON Schema.
 *
 * `questionnaireId` moved out of the body and into the **path**, matching the
 * sibling create and delete endpoints (`/questionnaires/:id/requests`). All three
 * now agree on where a request collection lives, and the id can no longer
 * disagree with the resource being addressed.
 *
 * That move is what makes the scoping fix expressible. The legacy service looked
 * the questionnaire up with an **unscoped** `getById`, so any authenticated
 * caller holding `questionnaire-request:write` could send reminder emails on
 * another company's questionnaire — the legacy integration test pinned exactly
 * that. The service now scopes the lookup by `company_id`, as the sibling create,
 * delete and (still-legacy) due-date paths all already did. No caller notices:
 * both frontends only ever remind on the questionnaire currently on screen.
 *
 * `requestIds` is **required but deliberately uncapped and un-`.min()`ed** — the
 * opposite of `BulkDeleteQuestionnaireRequestsBodySchema`. Legacy declared
 * `minItems: 0` and treated an empty array as "remind every request on this
 * questionnaire", which is the old frontend's bell button with nothing selected.
 * A missing key is still a 400; an empty array is the remind-all path.
 *
 * Duplicates are rejected rather than tolerated: the service compares found rows
 * against the raw submitted count, so `["a","a"]` matches one row against two ids
 * and 404s — as it does today. Same call as `questionnaire-delete.service.ts`;
 * de-duplicating would silently widen what the endpoint accepts, and neither
 * frontend can produce duplicates (both build the array from a selection Set).
 */
export const RemindQuestionnaireRequestsBodySchema = z.object({
  requestIds: z.array(z.string().min(1)),
});

export type RemindQuestionnaireRequestsBody = z.infer<
  typeof RemindQuestionnaireRequestsBodySchema
>;
